import { ChangeEvent, ClipboardEvent, FC, RefObject } from 'react';

import { Box, Button, FormLabel, InputBase, styled, Typography, useTheme } from '@mui/material';
import { useField, useFormikContext } from 'formik';

type InputComponentProps = {
    name: string;
    placeholder: string;
    disabled?: boolean;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    handlePaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
    inputRef?: RefObject<HTMLInputElement>;
    inputProps?: object;
};

export type MetaType = {
    error?: string;
    touched?: boolean;
    value?: string;
};
type NPIDualInputProps = {
    title: string;
    component1: InputComponentProps;
    component2: InputComponentProps;
    onValidate?: ({
        meta1,
        meta2,
        triggerState,
    }: {
        meta1: MetaType;
        meta2: MetaType;
        triggerState: 'click' | 'blur';
    }) => void;
    showValidateButton?: boolean;
};

const InputComponent = styled(InputBase)({
    width: '100px',
    '&:focus': {
        backgroundColor: 'transparent',
        outline: 'none',
    },
    flex: 1,
    padding: '8px 16px',
});

const NPIDualInput: FC<NPIDualInputProps> = ({
    title,
    component1: {
        name: name1,
        placeholder: placeholder1,
        disabled: disabled1 = false,
        onChange: onChange1,
        handlePaste: handlePaste1,
        inputRef: inputRef1,
        inputProps: inputProps1,
    },
    component2: {
        name: name2,
        placeholder: placeholder2,
        disabled: disabled2 = false,
        onChange: onChange2,
        handlePaste: handlePaste2,
        inputRef: inputRef2,
        inputProps: inputProps2,
    },
    onValidate,
    showValidateButton = true,
}) => {
    const [field1, meta1] = useField(name1);
    const [field2, meta2] = useField(name2);

    const { palette } = useTheme();
    const borderColor =
        (meta1.error || meta2.error) && (meta1.touched || meta2.touched)
            ? palette.error.main
            : palette.border.light;

    const { setTouched } = useFormikContext();

    const handleBlurSecond = async () => {
        if (disabled1 || disabled2) return;

        await setTouched({ [name1]: true, [name2]: true });

        if (showValidateButton) onValidate({ meta1, meta2, triggerState: 'blur' });
    };

    return (
        <Box mt={1}>
            <FormLabel sx={{ marginBottom: '8px', color: '#000', fontWeight: 600 }}>
                {title}
            </FormLabel>

            <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                border={`1px solid ${borderColor}`}
                height="51px"
                borderRadius="8px"
                overflow="hidden"
            >
                <InputComponent
                    {...field1}
                    placeholder={placeholder1}
                    disabled={disabled1}
                    onChange={onChange1}
                    onPaste={handlePaste1}
                    inputRef={inputRef1}
                    inputProps={{ ...inputProps1, 'data-testid': `${name1}-input` }}
                />
                <Typography sx={{ px: 1 }}>/</Typography>
                <InputComponent
                    {...field2}
                    onBlur={handleBlurSecond}
                    placeholder={placeholder2}
                    disabled={disabled2}
                    onChange={onChange2}
                    onPaste={handlePaste2}
                    inputRef={inputRef2}
                    inputProps={{ ...inputProps2, 'data-testid': `${name2}-input` }}
                />

                {showValidateButton && (
                    <Button
                        onClick={() => {
                            onValidate({ meta1, meta2, triggerState: 'click' });
                        }}
                        sx={{
                            bgcolor: `${borderColor}`,
                            color: 'white',
                            px: 3,
                            height: '100%',
                            borderRadius: 0,
                        }}
                    >
                        Validate
                    </Button>
                )}
            </Box>

            {(meta1.touched && meta1.error) || (meta2.touched && meta2.error) ? (
                <Typography color="error" variant="caption">
                    {meta1.error || meta2.error}
                </Typography>
            ) : null}
        </Box>
    );
};

export default NPIDualInput;
